// @ts-nocheck
import React from 'react';
import { Loader, AlertCircle, CheckCircle, Clock } from 'lucide-react';

/**
 * Composant de gestion des états de chargement centralisé
 * Améliore l'UX en fournissant des feedbacks visuels cohérents
 */
const LoadingStates = {
  // État de chargement simple
  Loading: ({ message = 'Chargement...', size = 'medium' }) => {
    const sizeClasses = {
      small: 'w-4 h-4',
      medium: 'w-6 h-6',
      large: 'w-8 h-8'
    };

    return (
      <div className="loading-container">
        <Loader className={`animate-spin ${sizeClasses[size]}`} />
        {message && <span className="loading-message">{message}</span>}
        
        <style>{`
          .loading-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 12px;
            padding: 20px;
            color: #6b7280;
          }
          
          .loading-message {
            font-size: 14px;
            text-align: center;
          }
        `}</style>
      </div>
    );
  },

  // État de chargement avec skeleton
  Skeleton: ({ lines = 3, height = 20, width = '100%' }) => (
    <div className="skeleton-container">
      {Array.from({ length: lines }).map((_, index) => (
        <div
          key={index}
          className="skeleton-line"
          style={{ 
            height: `${height}px`,
            width: index === lines - 1 ? '60%' : width,
            animationDelay: `${index * 0.1}s`
          }}
        />
      ))}
      
      <style>{`
        .skeleton-container {
          display: flex;
          flex-direction: column;
          gap: 8px;
          padding: 16px;
        }
        
        .skeleton-line {
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          border-radius: 4px;
          animation: shimmer 1.5s infinite;
        }
        
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>
    </div>
  ),

  // État d'erreur avec retry
  Error: ({ 
    message = 'Une erreur s\'est produite', 
    onRetry, 
    showRetry = true,
    details = null 
  }) => (
    <div className="error-state">
      <AlertCircle size={32} className="error-icon" />
      <h3 className="error-title">Erreur</h3>
      <p className="error-message">{message}</p>
      
      {details && (
        <details className="error-details">
          <summary>Détails techniques</summary>
          <pre>{details}</pre>
        </details>
      )}
      
      {showRetry && onRetry && (
        <button onClick={onRetry} className="retry-button">
          <Loader size={16} />
          Réessayer
        </button>
      )}
      
      <style>{`
        .error-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding: 32px 16px;
          gap: 16px;
        }
        
        .error-icon {
          color: #ef4444;
        }
        
        .error-title {
          font-size: 18px;
          font-weight: 600;
          color: #1f2937;
          margin: 0;
        }
        
        .error-message {
          color: #6b7280;
          margin: 0;
          max-width: 400px;
        }
        
        .error-details {
          text-align: left;
          width: 100%;
          max-width: 400px;
        }
        
        .error-details summary {
          cursor: pointer;
          color: #374151;
          font-weight: 500;
          margin-bottom: 8px;
        }
        
        .error-details pre {
          background: #f3f4f6;
          padding: 12px;
          border-radius: 6px;
          font-size: 12px;
          overflow-x: auto;
          color: #dc2626;
        }
        
        .retry-button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background: #8B5CF6;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          transition: background 0.2s;
        }
        
        .retry-button:hover {
          background: #7C3AED;
        }
      `}</style>
    </div>
  ),

  // État de succès
  Success: ({ message = 'Opération réussie', icon = CheckCircle }) => {
    const IconComponent = icon;
    
    return (
      <div className="success-state">
        <IconComponent size={32} className="success-icon" />
        <p className="success-message">{message}</p>
        
        <style>{`
          .success-state {
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            padding: 24px 16px;
            gap: 12px;
          }
          
          .success-icon {
            color: #10b981;
          }
          
          .success-message {
            color: #059669;
            font-weight: 500;
            margin: 0;
          }
        `}</style>
      </div>
    );
  },

  // État vide (aucune donnée)
  Empty: ({ 
    message = 'Aucune donnée disponible', 
    icon = Clock,
    action = null 
  }) => {
    const IconComponent = icon;
    
    return (
      <div className="empty-state">
        <IconComponent size={48} className="empty-icon" />
        <h3 className="empty-title">Aucune donnée</h3>
        <p className="empty-message">{message}</p>
        
        {action && (
          <div className="empty-action">
            {action}
          </div>
        )}
        
        <style>{`
          .empty-state {
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            padding: 48px 16px;
            gap: 16px;
          }
          
          .empty-icon {
            color: #9ca3af;
          }
          
          .empty-title {
            font-size: 18px;
            font-weight: 600;
            color: #374151;
            margin: 0;
          }
          
          .empty-message {
            color: #6b7280;
            margin: 0;
            max-width: 300px;
          }
          
          .empty-action {
            margin-top: 8px;
          }
        `}</style>
      </div>
    );
  },

  // État de chargement progressif
  Progress: ({ 
    current, 
    total, 
    message = 'Traitement en cours...',
    showPercentage = true 
  }) => {
    const percentage = total > 0 ? Math.round((current / total) * 100) : 0;
    
    return (
      <div className="progress-state">
        <div className="progress-header">
          <span className="progress-message">{message}</span>
          {showPercentage && (
            <span className="progress-percentage">{percentage}%</span>
          )}
        </div>
        
        <div className="progress-bar">
          <div 
            className="progress-fill"
            style={{ width: `${percentage}%` }}
          />
        </div>
        
        <div className="progress-details">
          {current} / {total} éléments traités
        </div>
        
        <style>{`
          .progress-state {
            padding: 24px;
            width: 100%;
            max-width: 400px;
          }
          
          .progress-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 12px;
          }
          
          .progress-message {
            font-weight: 500;
            color: #374151;
          }
          
          .progress-percentage {
            font-weight: 600;
            color: #8B5CF6;
          }
          
          .progress-bar {
            width: 100%;
            height: 8px;
            background: #e5e7eb;
            border-radius: 4px;
            overflow: hidden;
            margin-bottom: 8px;
          }
          
          .progress-fill {
            height: 100%;
            background: linear-gradient(90deg, #8B5CF6, #6D28D9);
            border-radius: 4px;
            transition: width 0.3s ease;
          }
          
          .progress-details {
            font-size: 12px;
            color: #6b7280;
            text-align: center;
          }
        `}</style>
      </div>
    );
  }
};

export default LoadingStates;
